import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useRef } from "react";
import { useLanguage } from "@/context/LanguageContext";

const HERO_IMAGE =
    "https://customer-assets-wrfwihn1.emergentagent.net/job_wear-crafted/artifacts/dbopze6z_DJI_20260923_124401_Edit_Composited_Photo.webp";

const letterAnim = {
    hidden: { y: "115%", rotate: 4 },
    visible: (i) => ({
        y: 0,
        rotate: 0,
        transition: { delay: 0.15 + i * 0.07, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
    }),
};

export default function Hero() {
    const { t } = useLanguage();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

    return (
        <section ref={ref} className="relative min-h-screen flex flex-col pt-16 md:pt-20" data-testid="hero-section">
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
                {/* Left: type */}
                <div className="flex flex-col justify-center px-5 md:px-10 py-16 lg:py-0 order-2 lg:order-1">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.7 }}
                        className="text-xs md:text-sm tracking-[0.35em] uppercase text-zinc-500 mb-6"
                        data-testid="hero-eyebrow"
                    >
                        {t("hero.eyebrow")}
                    </motion.p>
                    <h1 className="font-display font-extrabold uppercase leading-[0.85] tracking-tighter text-[20vw] lg:text-[9vw]" data-testid="hero-title">
                        {"SEVA".split("").map((letter, i) => (
                            <span key={i} className="inline-block overflow-hidden align-bottom">
                                <motion.span
                                    className="inline-block"
                                    custom={i}
                                    variants={letterAnim}
                                    initial="hidden"
                                    animate="visible"
                                >
                                    {letter}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="mt-10 flex flex-wrap gap-4"
                    >
                        <Link
                            to="/shop"
                            data-testid="hero-cta-shop"
                            className="group bg-black text-white px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold flex items-center gap-3 transition-colors duration-300 hover:bg-zinc-800"
                        >
                            {t("hero.ctaShop")}
                            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                        </Link>
                        <Link
                            to="/lookbook"
                            data-testid="hero-cta-lookbook"
                            className="group border border-black px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold flex items-center gap-3 transition-colors duration-300 hover:bg-black hover:text-white"
                        >
                            {t("hero.ctaLookbook")}
                        </Link>
                    </motion.div>
                </div>

                {/* Right: image with parallax + clip reveal */}
                <div className="relative order-1 lg:order-2 h-[50vh] lg:h-auto overflow-hidden bg-zinc-100">
                    <motion.div
                        initial={{ clipPath: "inset(100% 0 0 0)" }}
                        animate={{ clipPath: "inset(0% 0 0 0)" }}
                        transition={{ delay: 0.3, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute inset-0"
                    >
                        <motion.img
                            src={HERO_IMAGE}
                            alt="Editoriale Seva — capo fatto a mano"
                            style={{ y: imageY, scale: 1.15 }}
                            className="w-full h-full object-cover"
                            data-testid="hero-image"
                        />
                    </motion.div>
                </div>
            </div>

            {/* Scroll hint */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-zinc-400"
            >
                <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
                    <ArrowDown size={18} />
                </motion.span>
            </motion.div>
        </section>
    );
}
